import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { me, logout as apiLogout } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Read the SRES session token from either localStorage ("Remember me" on)
 * or sessionStorage (session-only). Also migrates the legacy `of_token`
 * key transparently on first read so existing sessions survive upgrades.
 */
function readToken() {
  let token = localStorage.getItem("sres_token") || sessionStorage.getItem("sres_token");
  if (!token) {
    const legacy = localStorage.getItem("of_token") || sessionStorage.getItem("of_token");
    if (legacy) {
      localStorage.setItem("sres_token", legacy);
      localStorage.removeItem("of_token");
      sessionStorage.removeItem("of_token");
      token = legacy;
    }
  }
  return token;
}

function clearAllTokens() {
  localStorage.removeItem("sres_token");
  localStorage.removeItem("of_token");
  sessionStorage.removeItem("sres_token");
  sessionStorage.removeItem("of_token");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    const token = readToken();
    if (!token) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const data = await me();
      setUser(data);
    } catch {
      clearAllTokens();
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /**
   * Persist the JWT.
   *   remember=true  → localStorage (survives browser restarts)
   *   remember=false → sessionStorage (cleared when the tab/browser closes)
   */
  const login = (token, u, remember = true) => {
    // Clear any stale token in the other store first.
    clearAllTokens();
    if (remember) {
      localStorage.setItem("sres_token", token);
    } else {
      sessionStorage.setItem("sres_token", token);
    }
    setUser(u);
  };
  const logout = () => {
    clearAllTokens();
    // Fire-and-forget cookie clear on the server — safe if already logged out.
    apiLogout().catch(() => {});
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, checking, login, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
